import { useEffect, useState } from 'react';
import { Box, IconButton, Typography, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Link } from '@backstage/core-components';
import { useDownDetectorAlerts } from './useDownDetectorAlerts';
import { useKubernetesAlerts } from './useKubernetesAlerts';
import type { KubernetesAlert } from './useKubernetesAlerts';
import { useOrcaAlerts } from './useOrcaAlerts';
import type { OrcaAlert } from './useOrcaAlerts';
import { useSecuritySloBreaches, SloBreachTeam } from './useSecuritySloBreaches';

type AlertTone = 'red' | 'amber' | 'blue';

interface PlaceholderAlert {
  id: string;
  tone: 'red' | 'amber';
  label: string;
}

const DOWN_DETECTOR_PATH = '/external-down-detector';

// Exact per-tone colours from the approved design source (My Day.dc.html
// `toneMap`). SECURITY (red) is wired to security-insights-backend,
// KUBERNETES (amber) is wired to useKubernetesAlerts,
// ORKA (red) is wired to useOrcaAlerts (PAT-625),
// BACKSTAGE (blue) is wired to external-down-detector-backend.
// No remaining placeholders.
const PLACEHOLDER_ALERTS: PlaceholderAlert[] = [];

const TONE_COLORS: Record<
  AlertTone,
  {
    border: string;
    background: string;
    tagBackground: string;
    tagColor: string;
  }
> = {
  red: {
    border: '#E5484D',
    background: '#FDECEC',
    tagBackground: '#F7CFCF',
    tagColor: '#C0272D',
  },
  amber: {
    border: '#E0A526',
    background: '#FEF6E7',
    tagBackground: '#F4E1AE',
    tagColor: '#8A5A00',
  },
  blue: {
    border: '#4C8DD6',
    background: '#EAF3FC',
    tagBackground: '#CFE3F7',
    tagColor: '#1C5FA8',
  },
};

const LS_KEY = 'myday-dismissed-slo-breaches';

type DismissedMap = Record<string, number>;

function readDismissedMap(): DismissedMap {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    return parsed as DismissedMap;
  } catch {
    return {};
  }
}

function buildSecuritySubtitle(team: SloBreachTeam): string {
  const parts: string[] = [];
  if (team.critical > 0) parts.push(`${team.critical} Critical`);
  if (team.high > 0) parts.push(`${team.high} High`);
  if (parts.length > 0) {
    return `${parts.join(' · ')} past remediation deadline`;
  }
  return `${team.total} past remediation deadline`;
}

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 10,
    borderLeft: '5px solid',
    marginBottom: theme.spacing(1),
  },
  tag: {
    fontFamily: 'ui-monospace, Menlo, monospace',
    fontWeight: 700,
    fontSize: '10.5px',
    letterSpacing: 0.5,
    padding: theme.spacing(0.25, 1),
    borderRadius: 5,
    flexShrink: 0,
  },
  // Single-line row matching the approved mockup (My Day.dc.html option 1a,
  // the layout MyDayPage actually uses): title stays whole, description
  // truncates with an ellipsis rather than wrapping to a second line.
  linkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    flex: 1,
    textDecoration: 'none',
    color: 'inherit',
  },
  linkDescription: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const dismissPlaceholderKey = (id: string) => `placeholder:${id}`;
const dismissVendorKey = (id: string) => `vendor:${id}`;
const dismissK8sKey = (id: string) => `kubernetes:${id}`;
const dismissOrcaKey = (id: string) => `orca:${id}`;

function buildOrcaTitle(alert: OrcaAlert): string {
  return `${alert.totalHighSeverity} Orca alerts firing`;
}

function buildOrcaDescription(alert: OrcaAlert): string {
  // Spec (PAT-625) suggested "past remediation threshold" suffix, but that
  // phrase belongs to SLO-tracked findings (PAT-624 SECURITY). Orca Security
  // alertCounts are plain counters with no deadline semantics here, so the
  // suffix is intentionally omitted to avoid misleading the user.
  const parts: string[] = [];
  if (alert.critical > 0) parts.push(`${alert.critical} Critical`);
  if (alert.high > 0) parts.push(`${alert.high} High`);
  return `${alert.entityName}: ${parts.join(' · ')}`;
}

export const AlertBanners = () => {
  const classes = useStyles();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [dismissedSlo, setDismissedSlo] = useState<DismissedMap>(readDismissedMap);

  const { downVendors, degradedVendors } = useDownDetectorAlerts();
  const { alerts: k8sAlerts, overflowCount: k8sOverflowCount } = useKubernetesAlerts();
  const { alerts: orcaAlerts, overflowCount: orcaOverflowCount } = useOrcaAlerts();
  const { teams, overflowCount, loaded } = useSecuritySloBreaches();

  const dismiss = (key: string) => setDismissed(prev => new Set(prev).add(key));

  // Pure state update — localStorage is persisted separately via the effect below.
  const dismissSecurityTeam = (teamName: string, total: number) => {
    setDismissedSlo(prev => ({ ...prev, [teamName]: total }));
  };

  // Persist dismissedSlo to localStorage whenever it changes.
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(dismissedSlo));
    } catch {
      // localStorage unavailable in private browsing — silently ignore
    }
  }, [dismissedSlo]);

  // When a team resolves all its breaches (leaves teams), prune its dismiss entry
  // so if it later reappears with the same total count the banner surfaces again.
  // Guard on `loaded`: before the first poll completes teams=[], which would wipe
  // all dismissals from localStorage on every page load.
  useEffect(() => {
    if (!loaded) return;
    const activeNames = new Set(teams.map(t => t.name));
    setDismissedSlo(prev => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([name]) => activeNames.has(name)),
      );
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [teams, loaded]);

  // When a vendor leaves both downVendors and degradedVendors (fully recovered),
  // clear its dismiss key so the next incident surfaces the banner again.
  useEffect(() => {
    const activeKeys = new Set([
      ...downVendors.map(v => dismissVendorKey(v.id)),
      ...degradedVendors.map(v => dismissVendorKey(v.id)),
    ]);
    setDismissed(prev => {
      let changed = false;
      const next = new Set(prev);
      for (const key of next) {
        if (key.startsWith('vendor:') && !activeKeys.has(key)) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [downVendors, degradedVendors]);

  // When a Kubernetes alert clears (pod recovers), remove its dismiss key so
  // the same pod surfacing a new error is visible again.
  useEffect(() => {
    const activeK8sKeys = new Set(k8sAlerts.map(a => dismissK8sKey(a.id)));
    setDismissed(prev => {
      let changed = false;
      const next = new Set(prev);
      for (const key of next) {
        if (key.startsWith('kubernetes:') && !activeK8sKeys.has(key)) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [k8sAlerts]);

  // When an Orca alert clears (entity recovered), reset its dismiss key so
  // the same entity surfacing a new alert is visible again.
  useEffect(() => {
    const activeOrcaKeys = new Set(orcaAlerts.map(a => dismissOrcaKey(a.id)));
    setDismissed(prev => {
      let changed = false;
      const next = new Set(prev);
      for (const key of next) {
        if (key.startsWith('orca:') && !activeOrcaKeys.has(key)) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [orcaAlerts]);

  const visiblePlaceholders = PLACEHOLDER_ALERTS.filter(
    alert => !dismissed.has(dismissPlaceholderKey(alert.id)),
  );
  const visibleOrcaAlerts = orcaAlerts.filter(
    alert => !dismissed.has(dismissOrcaKey(alert.id)),
  );
  const visibleSecurityTeams = teams.filter(
    t => dismissedSlo[t.name] === undefined || t.total > dismissedSlo[t.name],
  );
  const visibleDownVendors = downVendors.filter(
    vendor => !dismissed.has(dismissVendorKey(vendor.id)),
  );
  const visibleDegradedVendors = degradedVendors.filter(
    vendor => !dismissed.has(dismissVendorKey(vendor.id)),
  );
  const visibleK8sAlerts = k8sAlerts.filter(
    alert => !dismissed.has(dismissK8sKey(alert.id)),
  );

  const redColors = TONE_COLORS.red;
  const amberColors = TONE_COLORS.amber;
  const blueColors = TONE_COLORS.blue;

  return (
    <>
      {visibleSecurityTeams.map(team => (
        <Box
          key={team.name}
          className={classes.row}
          style={{
            borderLeftColor: redColors.border,
            backgroundColor: redColors.background,
          }}
        >
          <Link
            to={`/security-insights?team=${encodeURIComponent(team.name)}&sloCheck=true`}
            className={classes.linkRow}
          >
            <span
              className={classes.tag}
              style={{
                backgroundColor: redColors.tagBackground,
                color: redColors.tagColor,
              }}
            >
              SECURITY
            </span>
            <Box>
              <Typography variant="body2" style={{ color: '#5c5866', fontWeight: 600 }}>
                {team.total} findings breached SLO
              </Typography>
              <Typography variant="caption" style={{ color: '#5c5866' }}>
                {buildSecuritySubtitle(team)}
              </Typography>
            </Box>
          </Link>
          <IconButton
            size="small"
            aria-label={`Dismiss SECURITY alert for ${team.name}`}
            onClick={() => dismissSecurityTeam(team.name, team.total)}
            style={{ marginLeft: 'auto' }}
          >
            <CloseIcon fontSize="small" style={{ color: '#a49fb0' }} />
          </IconButton>
        </Box>
      ))}

      {overflowCount > 0 && (
        <Box
          className={classes.row}
          style={{
            borderLeftColor: redColors.border,
            backgroundColor: redColors.background,
          }}
        >
          <Link
            to="/security-insights?sloCheck=true"
            className={classes.linkRow}
          >
            <span
              className={classes.tag}
              style={{
                backgroundColor: redColors.tagBackground,
                color: redColors.tagColor,
              }}
            >
              SECURITY
            </span>
            <Typography variant="body2" style={{ color: '#5c5866', fontWeight: 600 }}>
              {`...and ${overflowCount} more team${overflowCount === 1 ? '' : 's'} have open SLO breaches`}
            </Typography>
          </Link>
        </Box>
      )}

      {visiblePlaceholders.map(alert => {
        const colors = TONE_COLORS[alert.tone];
        return (
          <Box
            key={alert.id}
            className={classes.row}
            style={{
              borderLeftColor: colors.border,
              backgroundColor: colors.background,
            }}
          >
            <span
              className={classes.tag}
              style={{
                backgroundColor: colors.tagBackground,
                color: colors.tagColor,
              }}
            >
              {alert.label}
            </span>
            <Typography variant="body2" style={{ color: '#5c5866' }}>
              No data source configured yet
            </Typography>
            <IconButton
              size="small"
              aria-label={`Dismiss ${alert.label} alert`}
              onClick={() => dismiss(dismissPlaceholderKey(alert.id))}
              style={{ marginLeft: 'auto' }}
            >
              <CloseIcon fontSize="small" style={{ color: '#a49fb0' }} />
            </IconButton>
          </Box>
        );
      })}
      {visibleOrcaAlerts.map((alert: OrcaAlert) => (
        <Box
          key={alert.id}
          className={classes.row}
          style={{
            borderLeftColor: redColors.border,
            backgroundColor: redColors.background,
          }}
        >
          <Link to={alert.orcaPath} className={classes.linkRow}>
            <span
              className={classes.tag}
              style={{
                backgroundColor: redColors.tagBackground,
                color: redColors.tagColor,
              }}
            >
              ORKA
            </span>
            <Typography
              variant="body2"
              style={{ fontWeight: 700, color: '#241f2c', flexShrink: 0 }}
            >
              {buildOrcaTitle(alert)}
            </Typography>
            <Typography
              variant="body2"
              className={classes.linkDescription}
              style={{ color: '#5c5866' }}
            >
              {buildOrcaDescription(alert)}
            </Typography>
          </Link>
          <IconButton
            size="small"
            aria-label={`Dismiss ${alert.entityName} Orca alert`}
            onClick={() => dismiss(dismissOrcaKey(alert.id))}
            style={{ marginLeft: 'auto' }}
          >
            <CloseIcon fontSize="small" style={{ color: '#a49fb0' }} />
          </IconButton>
        </Box>
      ))}
      {orcaOverflowCount > 0 && (
        <Box
          className={classes.row}
          style={{
            borderLeftColor: redColors.border,
            backgroundColor: redColors.background,
          }}
        >
          <Link
            to={visibleOrcaAlerts[0]?.orcaPath ?? orcaAlerts[0].orcaPath}
            className={classes.linkRow}
          >
            <span
              className={classes.tag}
              style={{
                backgroundColor: redColors.tagBackground,
                color: redColors.tagColor,
              }}
            >
              ORKA
            </span>
            <Typography variant="body2" style={{ color: '#5c5866' }}>
              +{orcaOverflowCount} more Orca alerts
            </Typography>
          </Link>
        </Box>
      )}
      {visibleK8sAlerts.map((alert: KubernetesAlert) => (
        <Box
          key={alert.id}
          className={classes.row}
          style={{
            borderLeftColor: amberColors.border,
            backgroundColor: amberColors.background,
          }}
        >
          <Link to={alert.kubernetesPath} className={classes.linkRow}>
            <span
              className={classes.tag}
              style={{
                backgroundColor: amberColors.tagBackground,
                color: amberColors.tagColor,
              }}
            >
              KUBERNETES
            </span>
            <Typography
              variant="body2"
              style={{ fontWeight: 700, color: '#241f2c', flexShrink: 0 }}
            >
              {alert.title}
            </Typography>
            <Typography
              variant="body2"
              className={classes.linkDescription}
              style={{ color: '#5c5866' }}
            >
              {alert.detail}
            </Typography>
          </Link>
          <IconButton
            size="small"
            aria-label={`Dismiss ${alert.title} alert`}
            onClick={() => dismiss(dismissK8sKey(alert.id))}
            style={{ marginLeft: 'auto' }}
          >
            <CloseIcon fontSize="small" style={{ color: '#a49fb0' }} />
          </IconButton>
        </Box>
      ))}
      {k8sOverflowCount > 0 && (
        <Box
          className={classes.row}
          style={{
            borderLeftColor: amberColors.border,
            backgroundColor: amberColors.background,
          }}
        >
          <Link
            to={k8sAlerts[0]?.kubernetesPath ?? '/catalog'}
            className={classes.linkRow}
          >
            <span
              className={classes.tag}
              style={{
                backgroundColor: amberColors.tagBackground,
                color: amberColors.tagColor,
              }}
            >
              KUBERNETES
            </span>
            <Typography variant="body2" style={{ color: '#5c5866' }}>
              +{k8sOverflowCount} more Kubernetes issue
              {k8sOverflowCount === 1 ? '' : 's'} — tap to view
            </Typography>
          </Link>
        </Box>
      )}
      {visibleDownVendors.map(vendor => (
        <Box
          key={vendor.id}
          className={classes.row}
          style={{
            borderLeftColor: blueColors.border,
            backgroundColor: blueColors.background,
          }}
        >
          <Link to={DOWN_DETECTOR_PATH} className={classes.linkRow}>
            <span
              className={classes.tag}
              style={{
                backgroundColor: blueColors.tagBackground,
                color: blueColors.tagColor,
              }}
            >
              BACKSTAGE
            </span>
            <Typography
              variant="body2"
              style={{ fontWeight: 700, color: '#241f2c', flexShrink: 0 }}
            >
              {vendor.name} is currently down
            </Typography>
            <Typography
              variant="body2"
              className={classes.linkDescription}
              style={{ color: '#5c5866' }}
            >
              {`Our down detector has recognized an outage affecting ${vendor.name}. Tap here for more details.`}
            </Typography>
          </Link>
          <IconButton
            size="small"
            aria-label={`Dismiss ${vendor.name} alert`}
            onClick={() => dismiss(dismissVendorKey(vendor.id))}
            style={{ marginLeft: 'auto' }}
          >
            <CloseIcon fontSize="small" style={{ color: '#a49fb0' }} />
          </IconButton>
        </Box>
      ))}
      {visibleDegradedVendors.map(vendor => (
        <Box
          key={vendor.id}
          className={classes.row}
          style={{
            borderLeftColor: blueColors.border,
            backgroundColor: blueColors.background,
          }}
        >
          <Link to={DOWN_DETECTOR_PATH} className={classes.linkRow}>
            <span
              className={classes.tag}
              style={{
                backgroundColor: blueColors.tagBackground,
                color: blueColors.tagColor,
              }}
            >
              BACKSTAGE
            </span>
            <Typography
              variant="body2"
              style={{ fontWeight: 700, color: '#241f2c', flexShrink: 0 }}
            >
              {`${vendor.name} is experiencing an incident`}
            </Typography>
            <Typography
              variant="body2"
              className={classes.linkDescription}
              style={{ color: '#5c5866' }}
            >
              Some sub-components are affected. Tap here for more details.
            </Typography>
          </Link>
          <IconButton
            size="small"
            aria-label={`Dismiss ${vendor.name} alert`}
            onClick={() => dismiss(dismissVendorKey(vendor.id))}
            style={{ marginLeft: 'auto' }}
          >
            <CloseIcon fontSize="small" style={{ color: '#a49fb0' }} />
          </IconButton>
        </Box>
      ))}
    </>
  );
};
